var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { normalizeKeys } from "../functions";
// Lazy load API_service only when needed
const getAPIService = () => __awaiter(void 0, void 0, void 0, function* () {
    const module = yield import("../../xAppLib/providers/API_service");
    return module.default;
});
const ContentfulPageContext = createContext(undefined);
// Content types that support contentBlockStyles
const STYLED_CONTENT_TYPES = ["paragraph", "media", "customContentSection"];
function pageDataStyles(pageData) {
    const { fields } = pageData || {};
    const styleMap = {};
    if (fields === null || fields === void 0 ? void 0 : fields.pageSections) {
        fields.pageSections.forEach((section) => {
            // Add section-level styles
            if (section.fields && section.fields.contentBlockStyles) {
                styleMap[section.sys.id] = section.fields.contentBlockStyles;
            }
            // Check for nested content blocks within the section
            if (section.fields && section.fields.content && Array.isArray(section.fields.content)) {
                section.fields.content.forEach((contentBlock) => {
                    var _a, _b, _c;
                    const contentTypeId = (_c = (_b = (_a = contentBlock === null || contentBlock === void 0 ? void 0 : contentBlock.sys) === null || _a === void 0 ? void 0 : _a.contentType) === null || _b === void 0 ? void 0 : _b.sys) === null || _c === void 0 ? void 0 : _c.id;
                    // Only process content types that are in our styled list
                    if (contentTypeId && STYLED_CONTENT_TYPES.includes(contentTypeId)) {
                        if (contentBlock.fields && contentBlock.fields.contentBlockStyles) {
                            styleMap[contentBlock.sys.id] = contentBlock.fields.contentBlockStyles;
                        }
                    }
                });
            }
        });
    }
    return styleMap;
}
export const ContentfulPageProvider = ({ children, initialPreview = false, isStatic = false, pageData, }) => {
    const [isPreview] = useState(initialPreview);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [listOptions, setListOptions] = useState([]);
    const [contentBlockStyleMap, setContentBlockStyleMap] = useState(pageDataStyles(pageData));
    useEffect(() => {
        const fetchConfigurableOptions = () => __awaiter(void 0, void 0, void 0, function* () {
            // Skip API calls in static mode
            if (isStatic)
                return;
            setIsLoading(true);
            try {
                const API_service = yield getAPIService();
                const params = {
                    preview: isPreview,
                    content_type: "listOptions",
                };
                const response = yield API_service.load_data("cms/getEntries", params);
                if (response.status === "ok") {
                    const { entries } = response;
                    const formattedOptions = entries.items.map((item) => (Object.assign(Object.assign({}, item.fields), { key: normalizeKeys(item.fields.title) })));
                    setListOptions(formattedOptions);
                }
            }
            catch (error) {
                console.error("Failed to fetch configurable options:", error);
            }
            finally {
                setIsLoading(false);
            }
        });
        if (isPreview) {
            fetchConfigurableOptions();
        }
    }, [isPreview, isStatic]);
    useEffect(() => {
        setContentBlockStyleMap(pageDataStyles(pageData));
    }, [pageData]);
    const updateContentBlockStyleMap = (blockId, styles) => {
        setContentBlockStyleMap(prev => (Object.assign(Object.assign({}, prev), { [blockId]: Object.assign(Object.assign({}, prev[blockId]), styles) })));
    };
    const getContentBlockStyles = (blockId) => {
        return contentBlockStyleMap[blockId] || {};
    };
    // Preview styles for real-time updates before saving
    const [previewStyles, setPreviewStylesState] = useState(null);
    const setPreviewStyles = useCallback((blockId, styles) => {
        if (blockId === null) {
            setPreviewStylesState(null);
        }
        else {
            setPreviewStylesState({ blockId, styles: styles || {} });
        }
    }, []);
    const updateSectionStyles = useCallback((sectionId, contentBlockStyles) => __awaiter(void 0, void 0, void 0, function* () {
        // In static mode, just update local state without API call
        if (isStatic) {
            updateContentBlockStyleMap(sectionId, contentBlockStyles);
            return { success: true };
        }
        setIsSaving(true);
        try {
            const API_service = yield getAPIService();
            const response = yield API_service.load_data("cms/update-section-styles", {
                sectionId,
                contentBlockStyles,
            });
            if (response.res === "ok") {
                updateContentBlockStyleMap(sectionId, contentBlockStyles);
                return { success: true };
            }
            else {
                console.error("Failed to update section styles:", response);
                return { success: false, error: response.error_message || "Failed to update styles" };
            }
        }
        catch (error) {
            console.error("Error updating section styles:", error);
            return { success: false, error: error.message };
        }
        finally {
            setIsSaving(false);
        }
    }), [isStatic, updateContentBlockStyleMap]);
    return (_jsx(ContentfulPageContext.Provider, { value: {
            isPreview,
            isStatic,
            listOptions,
            isLoading,
            isSaving,
            updateContentBlockStyleMap,
            getContentBlockStyles,
            contentBlockStyleMap,
            updateSectionStyles,
            previewStyles,
            setPreviewStyles,
        }, children: children }));
};
export const useContentfulPage = () => {
    const context = useContext(ContentfulPageContext);
    if (context === undefined) {
        throw new Error("useContentfulPage must be used within a ContentfulPageProvider");
    }
    return context;
};
export default ContentfulPageContext;
